import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import { SERVICES } from "../data/content";
import { useSiteContent } from "../context/SiteContentContext";
import { CheckCircle2, AlertCircle, Leaf, ArrowRight } from "lucide-react";

export default function ServicesPage() {
  const navigate = useNavigate();
  const { services: svcContent } = useSiteContent();
  const hash = window.location.hash?.replace("#", "");

  return (
    <div data-testid="services-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
      <div className="max-w-2xl">
        <span className="inline-block bg-[#D9E4E1] text-slate-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
          Our Services
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl text-slate-900 leading-tight">
          Five core areas of care, one personal approach
        </h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Women's health is rarely about one symptom — it's about your whole story. Explore the
          areas Dr. Prathusha specialises in, and when you're ready, book your visit in just a few
          clicks.
        </p>
      </div>

      <Accordion
        type="single"
        collapsible
        defaultValue={hash || SERVICES[0].id}
        className="mt-10 space-y-4"
        data-testid="services-accordion"
      >
        {SERVICES.map((s) => (
          <AccordionItem
            key={s.id}
            value={s.id}
            id={s.id}
            className="bg-white rounded-2xl border border-[#EDF1EF] px-6 data-[state=open]:bg-[#FBFCFB] data-[state=open]:border-[#D9E4E1]"
          >
            <AccordionTrigger
              data-testid={`service-accordion-${s.id}`}
              className="hover:no-underline py-5"
            >
              <div className="text-left">
                <div className="font-heading font-semibold text-lg text-slate-800">{s.name}</div>
                <div className="text-sm text-slate-500 font-normal mt-0.5">{svcContent[s.id]?.tagline || s.tagline}</div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-6">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-6">
                <div>
                  <p className="text-slate-600 leading-relaxed">{svcContent[s.id]?.description || s.description}</p>

                  {s.holistic && (
                    <div className="mt-4 bg-[#D9E4E1]/40 border border-[#D9E4E1] rounded-xl p-4 text-sm text-slate-700 leading-relaxed flex gap-3">
                      <Leaf className="w-5 h-5 text-[#4A7065] shrink-0 mt-0.5" />
                      <span>{s.holistic}</span>
                    </div>
                  )}

                  <ul className="mt-5 space-y-2.5">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex gap-2.5 text-sm text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-[#5E8B7E] shrink-0 mt-0.5" />
                        {b}
                      </li>
                    ))}
                  </ul>

                  {s.preOpNote && (
                    <div className="mt-5 bg-[#E8DAB2]/40 border border-[#E8DAB2] rounded-xl p-4 text-sm text-slate-700 leading-relaxed flex gap-3" data-testid="service-preop-note">
                      <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                      <span>{s.preOpNote}</span>
                    </div>
                  )}

                  <Button
                    data-testid={`service-book-btn-${s.id}`}
                    onClick={() => navigate(`/booking?service=${s.id}`)}
                    className="mt-6 rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]"
                  >
                    Book this service <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <img
                  src={s.image}
                  alt={s.name}
                  className="rounded-2xl object-cover w-full h-44 md:h-full hidden md:block"
                  loading="lazy"
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="mt-12 bg-[#2C557E] rounded-2xl p-8 text-center text-white">
        <h2 className="font-heading text-2xl">Ready when you are</h2>
        <p className="text-blue-100 text-sm mt-2 max-w-lg mx-auto">
          Registered patients can pick a date and time slot that suits them. Consultation fees are
          shown transparently on your confirmation screen.
        </p>
        <Button
          data-testid="services-page-book-cta"
          onClick={() => navigate("/booking")}
          className="mt-5 rounded-full bg-[#E8DAB2] text-slate-800 hover:bg-[#dccd9e] px-8"
        >
          Book an Appointment
        </Button>
      </div>
    </div>
  );
}
